import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  UserCircleIcon,
  ShieldCheckIcon,
  Cog6ToothIcon,
  CheckCircleIcon,
  XCircleIcon,
  CalendarIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { cn } from '@/utils/cn';
import { useAuth } from '@/stores/auth';
import PersonalInfoForm from '@/components/profile/PersonalInfoForm';
import PasswordChangeForm from '@/components/profile/PasswordChangeForm';

type ProfileTab = 'personal' | 'security' | 'account';

interface Tab {
  id: ProfileTab;
  label: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  description: string;
}

const tabs: Tab[] = [
  {
    id: 'personal',
    label: 'Personal Information',
    icon: UserCircleIcon,
    description: 'Update your personal details',
  },
  {
    id: 'security',
    label: 'Security',
    icon: ShieldCheckIcon,
    description: 'Change password and security settings',
  },
  {
    id: 'account',
    label: 'Account Settings',
    icon: Cog6ToothIcon,
    description: 'Manage your account preferences',
  },
];

export default function Profile() {
  const [activeTab, setActiveTab] = useState<ProfileTab>('personal');
  const { user, isLoading } = useAuth();

  // Show loading state while auth is being checked
  if (isLoading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <Card>
          <CardContent className="p-12 text-center">
            <div className="flex flex-col items-center space-y-4">
              <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
              <p className="text-sm text-secondary-600 dark:text-secondary-400">Loading profile...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Only show login message if not loading AND no user
  if (!user) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <Card>
          <CardContent className="p-12 text-center">
            <p className="text-secondary-600 dark:text-secondary-400">
              Please log in to view your profile
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const getInitials = () => {
    return `${user.firstName?.[0] ?? ''}${user.lastName?.[0] ?? ''}`.toUpperCase();
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) {return 'Never';}
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatDateTime = (dateString?: string) => {
    if (!dateString) {return 'Never';}
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-secondary-900 dark:text-white mb-2">
          My Profile
        </h1>
        <p className="text-secondary-600 dark:text-secondary-400">
          Manage your personal information and account settings
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Profile Summary Sidebar */}
        <div className="lg:col-span-1 space-y-6">
          {/* Profile Picture & Basic Info */}
          <Card>
            <CardContent className="p-6">
              <div className="flex flex-col items-center text-center">
                {/* Avatar */}
                <div className="w-24 h-24 bg-primary-100 dark:bg-primary-900/20 rounded-full flex items-center justify-center mb-4">
                  <span className="text-3xl font-bold text-primary-700 dark:text-primary-300">
                    {getInitials()}
                  </span>
                </div>

                {/* Name & Email */}
                <h2 className="text-xl font-semibold text-secondary-900 dark:text-white mb-1">
                  {user.firstName} {user.lastName}
                </h2>
                <p className="text-sm text-secondary-600 dark:text-secondary-400 mb-4">
                  {user.email}
                </p>

                {/* Status Badges */}
                <div className="flex flex-wrap gap-2 justify-center">
                  {user.emailVerified ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-success-100 text-success-800 dark:bg-success-900/20 dark:text-success-300">
                      <CheckCircleIcon className="w-3 h-3 mr-1" />
                      Email Verified
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-warning-100 text-warning-800 dark:bg-warning-900/20 dark:text-warning-300">
                      <XCircleIcon className="w-3 h-3 mr-1" />
                      Unverified
                    </span>
                  )}

                  {user.mfaEnabled && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900/20 dark:text-primary-300">
                      <ShieldCheckIcon className="w-3 h-3 mr-1" />
                      MFA Enabled
                    </span>
                  )}

                  <span className={cn(
                    'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                    user.isActive
                      ? 'bg-success-100 text-success-800 dark:bg-success-900/20 dark:text-success-300'
                      : 'bg-danger-100 text-danger-800 dark:bg-danger-900/20 dark:text-danger-300'
                  )}>
                    {user.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Account Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Account Information</CardTitle>
            </CardHeader>
            <CardContent className="p-6 pt-0">
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <CalendarIcon className="w-5 h-5 text-secondary-400 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-secondary-600 dark:text-secondary-400">
                      Member Since
                    </p>
                    <p className="text-sm font-medium text-secondary-900 dark:text-white">
                      {formatDate(user.createdAt)}
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <ClockIcon className="w-5 h-5 text-secondary-400 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-secondary-600 dark:text-secondary-400">
                      Last Login
                    </p>
                    <p className="text-sm font-medium text-secondary-900 dark:text-white">
                      {formatDateTime(user.lastLoginAt)}
                    </p>
                  </div>
                </div>

                {user.phone && (
                  <div className="flex items-start space-x-3">
                    <svg
                      className="w-5 h-5 text-secondary-400 mt-0.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z"
                      />
                    </svg>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-secondary-600 dark:text-secondary-400">
                        Phone Number
                      </p>
                      <p className="text-sm font-medium text-secondary-900 dark:text-white">
                        {user.phone}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Navigation */}
          <Card>
            <CardContent className="p-4">
              <nav className="space-y-1">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;

                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={cn(
                        'w-full flex items-start p-3 rounded-lg transition-colors text-left',
                        isActive
                          ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                          : 'text-secondary-700 dark:text-secondary-300 hover:bg-secondary-100 dark:hover:bg-secondary-800'
                      )}
                    >
                      <Icon className={cn(
                        'w-5 h-5 mr-3 mt-0.5 flex-shrink-0',
                        isActive
                          ? 'text-primary-600 dark:text-primary-400'
                          : 'text-secondary-500 dark:text-secondary-400'
                      )} />
                      <div className="min-w-0 flex-1">
                        <div className={cn(
                          'font-medium text-sm',
                          isActive && 'font-semibold'
                        )}>
                          {tab.label}
                        </div>
                        <div className="text-xs text-secondary-600 dark:text-secondary-400 mt-0.5">
                          {tab.description}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </nav>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.2 }}
            >
              {activeTab === 'personal' && (
                <Card>
                  <CardHeader>
                    <CardTitle>Personal Information</CardTitle>
                    <CardDescription>
                      Update your personal details and contact information
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="p-6">
                    <PersonalInfoForm />
                  </CardContent>
                </Card>
              )}

              {activeTab === 'security' && (
                <Card>
                  <CardHeader>
                    <CardTitle>Security Settings</CardTitle>
                    <CardDescription>
                      Manage your password and security preferences
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="p-6">
                    <div className="space-y-6">
                      {/* Password Change Section */}
                      <div>
                        <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-4">
                          Change Password
                        </h3>
                        <PasswordChangeForm />
                      </div>

                      {/* MFA Section - Coming Soon */}
                      <div className="pt-6 border-t border-secondary-200 dark:border-secondary-700">
                        <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-2">
                          Two-Factor Authentication
                        </h3>
                        <p className="text-sm text-secondary-600 dark:text-secondary-400 mb-4">
                          Add an extra layer of security to your account
                        </p>
                        <div className="bg-secondary-50 dark:bg-secondary-800 rounded-lg p-4">
                          <p className="text-sm text-secondary-600 dark:text-secondary-400 text-center">
                            Two-factor authentication settings coming soon
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {activeTab === 'account' && (
                <Card>
                  <CardHeader>
                    <CardTitle>Account Settings</CardTitle>
                    <CardDescription>
                      Manage your account preferences and settings
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="p-6">
                    <div className="text-center py-12">
                      <Cog6ToothIcon className="w-16 h-16 mx-auto text-secondary-400 dark:text-secondary-600 mb-4" />
                      <p className="text-secondary-600 dark:text-secondary-400">
                        Account settings coming soon
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
